import React from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangle, Sparkles } from 'lucide-react';
import { PageHeader } from '../components/common/PageHeader';
import { ScoreChip } from '../components/common/ScoreChip';
import { EmptyState } from '../components/common/EmptyState';
import { usePlatform } from '../contexts/PlatformContext';
import { MEGA_MAJOR_STAGES, STAGE_META, summariseGate } from '../utils/stages';
import { entityName } from '../data/entities';
import { daysUntil } from '../utils/format';
import { cn } from '../utils/cn';
export function PortfolioBoard() {
  const {
    applications
  } = usePlatform();
  const records = applications.filter(record => record.track !== 'international' && record.status !== 'rejected');
  const stalled = records.filter(record => record.daysInStage > 100);
  const heldGates = records.filter(record => {
    const summary = summariseGate(record.gates[record.stage] ?? []);
    return summary.total > 0 && !summary.cleared;
  });
  return <div data-id="element-1467" data-mp="3uox0drf10sjo">
      <PageHeader eyebrow="Lifecycle" title="Mega & Major portfolio board" description="Every Mega and Major record grouped by its stage in the six-stage governed lifecycle." data-id="element-1468" data-mp="3up6hsqm09rdr" />

      <div className="space-y-4 p-6" data-id="element-1469" data-mp="7imjlwjsqx7b">
        <section className="rounded-sm border-l-4 border-uae-gold bg-uae-navy p-4 text-white" data-id="element-1470" data-mp="7hkvaqdrse7n">
          <p className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-[0.12em] text-white/60" data-id="element-1471" data-mp="cciegq4rk3y9">
            <Sparkles className="h-3.5 w-3.5" aria-hidden="true" data-id="element-1472" data-mp="m8lfttrsjyk5" />
            AI portfolio note
          </p>
          <p className="mt-2 max-w-4xl text-[13px] leading-relaxed text-white/85" data-id="element-1473" data-mp="r7y14a30kxkm">
            {heldGates.length} of {records.length} records are holding at their current gate, and{' '}
            {stalled.length} have been in stage for more than 100 days. The bottleneck sits at{' '}
            {STAGE_META[MEGA_MAJOR_STAGES.reduce((worst, stage) => {
            const count = records.filter(r => r.stage === stage).length;
            const worstCount = records.filter(r => r.stage === worst).length;
            return count > worstCount ? stage : worst;
          }, MEGA_MAJOR_STAGES[0])].label}
            , where the largest number of records is concentrated. Gate overrides remain the only
            way to advance a held record, and each is attributed on the audit timeline.
          </p>
        </section>

        {records.length === 0 ? <EmptyState title="No Mega or Major records live" description="Records appear on the board once they enter the Assessment stage." data-id="element-1474" data-mp="i54jrk9ln0x0" /> : <div className="grid gap-3 lg:grid-cols-3 2xl:grid-cols-6" data-id="element-1475" data-mp="rws7pb6y5ztq">
            {MEGA_MAJOR_STAGES.map(stage => {
          const column = records.filter(record => record.stage === stage);
          return <section key={stage} className="rounded-sm border border-border bg-card" aria-label={`${STAGE_META[stage].label} stage`} data-id="element-1476" data-mp="10y8e77un5hso">
                  <header className="border-b border-border px-3 py-2.5" data-id="element-1477" data-mp="1pf7ftj3n07mz">
                    <p className="text-[10px] font-semibold uppercase tracking-[0.1em] text-muted-foreground" data-id="element-1478" data-mp="1bgzd0ujx6c3w">
                      Stage {STAGE_META[stage].index}
                    </p>
                    <div className="mt-0.5 flex items-center justify-between gap-2" data-id="element-1479" data-mp="11qxpq4ze4xq1">
                      <h2 className="text-[13px] font-semibold" data-id="element-1480" data-mp="16iplctxeltn1">{STAGE_META[stage].label}</h2>
                      <span className="font-mono text-[12px] text-muted-foreground" data-id="element-1481" data-mp="rub6tlqmkgqn">
                        {column.length}
                      </span>
                    </div>
                  </header>
                  <div className="space-y-2 p-2" data-id="element-1482" data-mp="itwi0cegcjie">
                    {column.length === 0 && <p className="px-1 py-4 text-center text-[11px] text-muted-foreground" data-id="element-1483" data-mp="nqwogof7roa1">
                        No records
                      </p>}
                    {column.map(record => {
                const summary = summariseGate(record.gates[record.stage] ?? []);
                const sla = daysUntil(record.slaDueOn);
                return <Link key={record.id} to={`/lifecycle/${record.id}`} className="block rounded-sm border border-border bg-background p-2.5 transition-colors hover:border-uae-leaf" data-id="element-1484" data-mp="1ysoegwabbbb">
                          <p className="text-[12px] font-medium leading-snug" data-id="element-1485" data-mp="3j77atb95eyzs">
                            {record.eventName}
                          </p>
                          <p className="mt-0.5 truncate text-[11px] text-muted-foreground" data-id="element-1486" data-mp="3f8qcnh7pgyui">
                            {entityName(record.entityId)}
                          </p>
                          <p className="mt-1 truncate text-[11px] text-muted-foreground" data-id="element-1487" data-mp="1dfsauvvyavxf">
                            {record.hostBody}
                          </p>
                          <div className="mt-2 flex items-center justify-between gap-2" data-id="element-1488" data-mp="30aovomxc67na">
                            <ScoreChip score={record.aiScore} data-id="element-1489" data-mp="3526y7ff0cgad" />
                            <span className={cn('font-mono text-[11px]', summary.cleared ? 'text-uae-leaf' : 'text-muted-foreground')} data-id="element-1490" data-mp="354t65g5t8xt4">
                              gate {summary.percent}%
                            </span>
                          </div>
                          <div className="mt-1.5 flex items-center justify-between gap-2 text-[11px]" data-id="element-1491" data-mp="2qdarpmslnbbv">
                            <span className="text-muted-foreground" data-id="element-1492" data-mp="oh3ai127psb">
                              {record.daysInStage}d in stage
                            </span>
                            {sla < 0 && <span className="inline-flex items-center gap-1 text-uae-red" data-id="element-1493" data-mp="21px3e52qaaiz">
                                <AlertTriangle className="h-3 w-3" aria-hidden="true" data-id="element-1494" data-mp="1xpeb69wxb2g9" />
                                overdue
                              </span>}
                          </div>
                        </Link>;
              })}
                  </div>
                </section>;
        })}
          </div>}
      </div>
    </div>;
}